import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Lock, Eye, EyeOff, CheckCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function handleGoogleLogin() {
  const redirectUrl = window.location.origin + '/dashboard';
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [contextMessage, setContextMessage] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const googleError = location.state?.error;

  // Pull contextual message from sessionStorage (set by other pages e.g., She Leads)
  if (typeof window !== "undefined" && !contextMessage) {
    const msg = sessionStorage.getItem("login_message");
    if (msg) {
      setContextMessage(msg);
      sessionStorage.removeItem("login_message");
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (err) {
      setError(err?.response?.data?.detail || "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row pt-16">
      <div className="lg:w-1/2 bg-navy flex flex-col items-center justify-center p-10 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "radial-gradient(#C9A84C 1px, transparent 1px)", backgroundSize: "20px 20px" }} />
        <div className="relative z-10 text-center">
          <Link to="/" className="inline-block mb-8">
            <span className="font-playfair text-3xl"><span className="text-white font-bold">Role</span><span className="text-gold font-bold">IQ</span></span>
          </Link>
          <p className="font-playfair text-gold italic text-lg mb-10">Map Every Role. Grow Every Professional.</p>
          <div className="space-y-4 max-w-xs mx-auto">
            {["Generate frameworks in 30 seconds", "Save and access your map history", "Join a community of HR professionals"].map((t) => (
              <div key={t} className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-gold flex-shrink-0" />
                <span className="font-dm text-white/80 text-sm text-left">{t}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="lg:w-1/2 bg-white flex items-center justify-center p-10">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <h1 className="font-playfair text-navy text-3xl font-bold mb-2">Welcome back</h1>
          <p className="font-dm text-navy/50 mb-8">Log in to your RoleIQ account</p>
          {(error || googleError) && <div className="bg-red-50 border border-red-200 text-red-700 font-dm text-sm px-4 py-3 rounded-lg mb-4">{error || googleError}</div>}
          {contextMessage && <div className="bg-gold/10 border border-gold/30 text-navy font-dm text-sm px-4 py-3 rounded-lg mb-4" data-testid="login-context-message">{contextMessage}</div>}

          {/* Google Sign In */}
          <button onClick={handleGoogleLogin} data-testid="google-login-btn"
            className="w-full flex items-center justify-center gap-3 border border-navy/20 rounded-lg py-3 mb-4 hover:bg-gray-50 transition-colors">
            <svg width="20" height="20" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.6 33.4 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 8 3l5.7-5.7C34 6 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.2-2.7-.4-3.9z"/><path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.5 15.5 18.8 12 24 12c3.1 0 5.8 1.2 8 3l5.7-5.7C34 6 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.2 26.7 36 24 36c-5.2 0-9.6-3.5-11.1-8.2l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C36.7 39.4 44 34 44 24c0-1.3-.2-2.7-.4-3.9z"/></svg>
            <span className="font-dm text-navy text-sm font-medium">Continue with Google</span>
          </button>

          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1 h-px bg-navy/10" />
            <span className="font-dm text-navy/40 text-xs">or</span>
            <div className="flex-1 h-px bg-navy/10" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-navy/30" />
              <input type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email"
                className="w-full pl-12 pr-4 py-3 border border-navy/20 rounded-lg font-dm text-sm focus:border-gold focus:outline-none" />
            </div>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-navy/30" />
              <input type={showPass ? "text" : "password"} required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password"
                className="w-full pl-12 pr-12 py-3 border border-navy/20 rounded-lg font-dm text-sm focus:border-gold focus:outline-none" />
              <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-4 top-1/2 -translate-y-1/2 text-navy/30 hover:text-navy">
                {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <button type="submit" disabled={loading} data-testid="login-submit"
              className="w-full bg-navy text-white font-dm font-bold py-3 rounded-lg hover:bg-gold hover:text-navy transition-colors disabled:opacity-50">
              {loading ? "Logging in..." : "Login"}
            </button>
          </form>
          <p className="text-center mt-6 font-dm text-sm text-navy/50">
            Don't have an account? <Link to="/signup" className="text-gold font-semibold hover:underline">Sign up free</Link>
          </p>
          <p className="text-center mt-2 font-dm text-sm">
            <Link to="/reset-password" className="text-navy/40 hover:text-gold transition-colors">Forgot password?</Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
